/**
 * Dates: the API speaks UTC ISO with "Z" (DateTime) and "yyyy-MM-dd" (DateOnly).
 * The UI always shows Asia/Ho_Chi_Minh (UTC+7, no DST) and sends ISO UTC.
 */
export const TIME_ZONE = 'Asia/Ho_Chi_Minh';
const VN_OFFSET = '+07:00';

const dateTimeFmt = new Intl.DateTimeFormat('vi-VN', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const dateTimeShortFmt = new Intl.DateTimeFormat('vi-VN', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function parts(d: Date, withSeconds: boolean) {
  const p = Object.fromEntries((withSeconds ? dateTimeFmt : dateTimeShortFmt).formatToParts(d).map((x) => [x.type, x.value]));
  const hour = p.hour === '24' ? '00' : p.hour;
  return { day: p.day, month: p.month, year: p.year, hour, minute: p.minute, second: p.second };
}

/** "2026-11-04T03:05:09.000Z" → "04/11/2026 10:05:09" (seconds for audit) or "04/11/2026 10:05". */
export function formatDateTime(iso: string | null | undefined, opts: { seconds?: boolean } = {}): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = parts(d, !!opts.seconds);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}${opts.seconds ? `:${p.second}` : ''}`;
}

/** DateOnly "2026-11-04" → "04/11/2026". Also accepts full ISO (shown in VN date). */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  const p = parts(dt, false);
  return `${p.day}/${p.month}/${p.year}`;
}

/** Relative "x phút trước" for notifications. */
export function formatRelative(iso: string, now = Date.now()): string {
  const diff = Math.round((now - Date.parse(iso)) / 1000);
  if (diff < 60) return 'vừa xong';
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} ngày trước`;
  return formatDate(iso);
}

/**
 * `<input type="datetime-local">` value (interpreted as Vietnam time) → ISO UTC.
 * "2026-11-04T10:05" or "2026-11-04T10:05:09" → "2026-11-04T03:05:09.000Z". Empty → undefined.
 */
export function localInputToUtc(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const withSeconds = value.length === 16 ? `${value}:00` : value;
  const d = new Date(`${withSeconds}${VN_OFFSET}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** ISO UTC → value for `<input type="datetime-local" step="1">` in Vietnam time ("yyyy-MM-ddTHH:mm:ss"). */
export function utcToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = parts(d, true);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}`;
}

/** Today in Vietnam as DateOnly "yyyy-MM-dd". */
export function todayVn(now = new Date()): string {
  const p = parts(now, false);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Vietnamese calendar date (DateOnly) → start of that day in UTC ISO. */
export function dateOnlyStartUtc(date: string): string {
  return new Date(`${date}T00:00:00${VN_OFFSET}`).toISOString();
}

/** Vietnamese calendar date → end of that day (23:59:59) in UTC ISO. */
export function dateOnlyEndUtc(date: string): string {
  return new Date(`${date}T23:59:59${VN_OFFSET}`).toISOString();
}
